/**
 * Claves de cache. **Todas** las query keys del dashboard nacen acá.
 *
 * Motivo: sin un lugar único, dos módulos escriben `['users']` y
 * `['users', 'list']` para lo mismo, y ninguna invalidación funciona del todo.
 *
 * Convención para agregar un recurso:
 *
 *     export const productKeys = {
 *       all: ['products'] as const,
 *       list: (filters: ProductFilters) => [...productKeys.all, 'list', filters] as const,
 *       detail: (id: number) => [...productKeys.all, 'detail', id] as const,
 *     };
 *
 * Reglas:
 * - El primer elemento es el recurso, en plural y en inglés.
 * - Los filtros van **enteros** en la clave: dos filtros distintos son dos
 *   entradas de cache, no una que se pisa.
 * - Nunca metas el `tenant_id` en la clave: no lo elige el cliente, sale del
 *   JWT. Lo que sí hay que hacer al cambiar de sesión es `queryClient.clear()`
 *   — lo hace `AuthProvider` en el logout.
 */

export const authKeys = {
  all: ['auth'] as const,
  session: () => [...authKeys.all, 'session'] as const,
  users: (limit: number) => [...authKeys.all, 'users', { limit }] as const,
};

/**
 * Filtros del listado de usuarios (#70). Van **enteros** en la clave: buscar
 * "maría" y filtrar por `admin` son dos respuestas distintas y las dos
 * conviene tenerlas en cache, porque volver al filtro anterior es lo que más
 * se hace mientras se arma un equipo.
 */
export interface UserListFilters {
  limit: number;
  /** `admin | analyst | viewer`, o `null` para todos. */
  role: string | null;
  /** `null` = activos e inactivos. */
  active: boolean | null;
  /** Texto libre sobre correo y nombre. `null` = sin búsqueda. */
  q: string | null;
}

export const userKeys = {
  all: ['users'] as const,
  list: (filters: UserListFilters) => [...userKeys.all, 'list', filters] as const,
  detail: (id: number) => [...userKeys.all, 'detail', id] as const,
};

export const tenantKeys = {
  all: ['tenant'] as const,
  settings: (slug: string) => [...tenantKeys.all, 'settings', slug] as const,
};

/**
 * Mensajes de verificación configurables por marca (#90).
 *
 * Sin filtros y sin id: es un único recurso por tenant —los seis mensajes en
 * los tres idiomas— y se lee y se reemplaza entero. El slug NO va en la clave:
 * sale del claim `tid` del JWT, como en todo el resto del dashboard.
 */
export const verificationKeys = {
  all: ['verification'] as const,
  messages: () => [...verificationKeys.all, 'messages'] as const,
};

// ─────────────────────────────────────────── activación en planta (#41)

export const activationKeys = {
  all: ['activations'] as const,
  list: (limit: number) => [...activationKeys.all, 'list', { limit }] as const,
  detail: (id: number) => [...activationKeys.all, 'detail', id] as const,
  conflicts: (id: number, limit: number) =>
    [...activationKeys.all, 'conflicts', id, { limit }] as const,
  /** Lotes de producción que ofrece el selector de la consola de línea. */
  batchOptions: (limit: number) => [...activationKeys.all, 'batch-options', { limit }] as const,
};

// ──────────────────────────────────────── bandeja de alertas y casos (#42)

export const alertKeys = {
  all: ['alerts'] as const,
  /** Los filtros van enteros: dos combinaciones son dos entradas de cache. */
  list: (filters: Record<string, string | number | boolean | null | undefined>) =>
    [...alertKeys.all, 'list', filters] as const,
  detail: (id: number) => [...alertKeys.all, 'detail', id] as const,
  notes: (id: number, limit: number) => [...alertKeys.all, 'notes', id, { limit }] as const,
  history: (id: number, limit: number) => [...alertKeys.all, 'history', id, { limit }] as const,
  summary: (days: number) => [...alertKeys.all, 'summary', { days }] as const,
};

// ─────────────────────────────────────────── analítica y mapa (#43 y #44)

/**
 * Filtros del mapa de calor. Van enteros en la clave: el mapa a 90 días con
 * precisión 5 y el de 7 días con precisión 6 son dos respuestas distintas y
 * las dos conviene tenerlas en cache — volver al filtro anterior es lo que
 * más se hace mientras se investiga, y con una sola entrada cada ida y vuelta
 * costaría una consulta.
 *
 * Se declaran acá y no en `routes/map/` porque `keys.ts` es el único lugar
 * donde nacen las claves; el módulo importa el tipo de acá.
 */
export interface HeatmapKeyFilters {
  /** Ventana en días. La API tolera hasta 400. */
  days: number;
  /** Geohash: 1 = continente, 6 = ~1,2 km (el grano máximo que existe). */
  precision: number;
  /** `all`, `genuine`, `suspicious`, o un resultado concreto. */
  layer: string;
  /** Umbral de k-anonimato. Nunca por debajo de 3. */
  minScans: number;
  /** Tope de celdas de la respuesta. */
  limit: number;
}

/** Ventana de las vistas de analítica. Sólo período: la API no filtra más. */
export interface AnalyticsKeyWindow {
  days: number;
}

export const analyticsKeys = {
  all: ['analytics'] as const,
  heatmap: (filters: HeatmapKeyFilters) => [...analyticsKeys.all, 'heatmap', filters] as const,
  overview: (window: AnalyticsKeyWindow) => [...analyticsKeys.all, 'overview', window] as const,
  timeseries: (window: AnalyticsKeyWindow) =>
    [...analyticsKeys.all, 'timeseries', window] as const,
};

export const exportKeys = {
  all: ['exports'] as const,
  list: (limit: number) => [...exportKeys.all, 'list', { limit }] as const,
  detail: (id: number) => [...exportKeys.all, 'detail', id] as const,
};

// ───────────────────────────────────────────────── catálogo y etiquetas (#38–#40)

/** Filtros del listado de productos. Ninguno es `tenant_id`: sale del JWT. */
export interface ProductListFilters {
  limit: number;
}

export const productKeys = {
  all: ['products'] as const,
  list: (filters: ProductListFilters) => [...productKeys.all, 'list', filters] as const,
  detail: (id: number) => [...productKeys.all, 'detail', id] as const,
};

export interface BatchListFilters {
  limit: number;
  /** `null` = todos los productos. */
  productId: number | null;
  /** `open | released | recalled | closed`, o `null` para todos. */
  status: string | null;
}

export const batchKeys = {
  all: ['batches'] as const,
  list: (filters: BatchListFilters) => [...batchKeys.all, 'list', filters] as const,
  detail: (id: number) => [...batchKeys.all, 'detail', id] as const,
};

/**
 * Pedidos de rollos del CLIENTE (#142). Aparte de `labelOrderKeys` porque son
 * dos objetos distintos y de dos lados distintos del mostrador: la orden es la
 * compra de XYVONA al impresor, el pedido es lo que la marca le pide a XYVONA.
 * Invalidarlos juntos escondería que resolver un pedido no cambia una orden.
 */
export const rollRequestKeys = {
  all: ['rollRequests'] as const,
  list: () => [...rollRequestKeys.all, 'list'] as const,
};

export interface LabelOrderListFilters {
  limit: number;
  /** `draft | generating | generated | cancelled`, o `null` para todos. */
  status: string | null;
}

export const labelOrderKeys = {
  all: ['labelOrders'] as const,
  list: (filters: LabelOrderListFilters) => [...labelOrderKeys.all, 'list', filters] as const,
  detail: (id: number) => [...labelOrderKeys.all, 'detail', id] as const,
  /**
   * La URL firmada del archivo del impresor. Va aparte del detalle porque
   * **vence**: cachearla junto con la orden la dejaría en memoria mucho
   * después de que el objeto ya no se pueda bajar con ella.
   */
  printFile: (id: number) => [...labelOrderKeys.all, 'printFile', id] as const,
  /** El veredicto depende de si se comprobó una muestra o el archivo entero. */
  verify: (id: number, full: boolean) =>
    [...labelOrderKeys.all, 'verify', id, { full }] as const,
};

export interface RollListFilters {
  limit: number;
  /** Estado de custodia, o `null` para todos. */
  state: string | null;
  /** Orden de etiquetas a la que pertenece, o `null`. */
  orderId: number | null;
}

export const rollKeys = {
  all: ['rolls'] as const,
  list: (filters: RollListFilters) => [...rollKeys.all, 'list', filters] as const,
  detail: (id: number) => [...rollKeys.all, 'detail', id] as const,
  custody: (id: number, limit: number) => [...rollKeys.all, 'custody', id, { limit }] as const,
};

// ────────────────────────── gestión de la etiqueta individual (#89)

/**
 * Filtros del listado de etiquetas. **El ámbito no es opcional**: la API
 * devuelve 400 sin `batch_id`, `roll_id` ni `code`, porque `unit` tiene
 * cientos de millones de filas y no se lista entera.
 *
 * Los tres van en la clave aunque sólo uno esté puesto a la vez: es lo que
 * hace que volver del lote 12 al rollo 4 lea de cache en vez de repetir la
 * consulta, que es lo que más se hace mientras se investiga.
 */
export interface LabelUnitListFilters {
  limit: number;
  /** Ámbito por lote, o `null`. */
  batchId: number | null;
  /** Ámbito por rollo, o `null`. */
  rollId: number | null;
  /** Ámbito por código impreso, o `null`. */
  code: string | null;
  /** Nombre del estado (`stolen`, `compromised`…), o `null` para todos. */
  status: string | null;
  /** Los filtros de #162. Todos los aplica el servidor; ninguno la página. */
  productId: number | null;
  verdict: string | null;
  scanned: boolean | null;
  expiresBefore: string | null;
  withOpenAlerts: boolean | null;
}

/**
 * `labelUnitKeys` y no `unitKeys`: el recurso de la API es `/labels`, y la
 * palabra "unidad" ya está tomada por el `unit_id` interno, que en el
 * dashboard no existe. La ficha se identifica siempre por su CÓDIGO impreso,
 * así que la clave de detalle lleva el código y nunca un id.
 */
export const labelUnitKeys = {
  all: ['labelUnits'] as const,
  list: (filters: LabelUnitListFilters) => [...labelUnitKeys.all, 'list', filters] as const,
  detail: (code: string) => [...labelUnitKeys.all, 'detail', code] as const,
  /**
   * El QR va aparte del detalle: es un archivo que no cambia nunca —es
   * función del código y de la configuración del entorno— mientras que la
   * ficha cambia con cada acción. Con una sola entrada, marcar una etiqueta
   * comprometida volvería a bajar una imagen idéntica.
   */
  qr: (code: string) => [...labelUnitKeys.all, 'qr', code] as const,
};

// ─────────────────────────────────────────────── auditoría (#45, B6.9)

/** Filtros del registro de auditoría. Van enteros en la clave. */
export interface AuditListFilters {
  limit: number;
  /** `batch`, `alert`, `api_key`, `user`… o `null` para todos. */
  entityType: string | null;
  /** Acción exacta (`batch.recalled`), o `null`. */
  action: string | null;
}

export const auditKeys = {
  all: ['audit'] as const,
  list: (filters: AuditListFilters) => [...auditKeys.all, 'list', filters] as const,
  head: () => [...auditKeys.all, 'head'] as const,
  /**
   * La verificación de la cadena es un barrido completo (la API le sube el
   * `statement_timeout` a tres minutos): tiene clave propia y no comparte
   * entrada con el listado, para que paginar no la vuelva a disparar.
   */
  verify: (fromSeq: number | null, toSeq: number | null) =>
    [...auditKeys.all, 'verify', { fromSeq, toSeq }] as const,
};

// ──────────────────────────────────── portal del consumidor (#46, B6.10)

/**
 * El portal es el **tercer** portal: sus endpoints (`/consumer/portal/*`) son
 * públicos y su sesión es la del comprador, no la del dashboard. Por eso las
 * claves llevan `consumer` adelante y nunca se mezclan con las del tenant.
 *
 * La configuración sí depende de la marca, y ahí el slug **sí** va en la
 * clave: a diferencia del resto de la API, `GET /consumer/portal/config` no
 * deriva el tenant de un JWT — lo recibe como parámetro, porque lo llama
 * alguien sin sesión.
 */
export const consumerPortalKeys = {
  all: ['consumerPortal'] as const,
  config: (tenantSlug: string) => [...consumerPortalKeys.all, 'config', tenantSlug] as const,
  me: () => [...consumerPortalKeys.all, 'me'] as const,
  codes: (limit: number) => [...consumerPortalKeys.all, 'codes', { limit }] as const,
};

// ──────────────────────────────────── reportes formales (#47, B6.11)

/**
 * Período del reporte, en días calendario del tenant (`AAAA-MM-DD`). No es
 * `days`: un reporte formal se archiva con fechas exactas, y "últimos 30
 * días" deja de significar lo mismo mañana.
 */
export interface ReportPeriod {
  since: string;
  until: string;
}

export const reportKeys = {
  all: ['reports'] as const,
  overview: (period: ReportPeriod) => [...reportKeys.all, 'overview', period] as const,
  timeseries: (period: ReportPeriod) => [...reportKeys.all, 'timeseries', period] as const,
  /**
   * Programaciones y corridas (#70). Cuelgan de `reportKeys.all` a propósito:
   * programar, pausar o forzar una corrida cambia las dos listas, y una sola
   * `invalidateQueries({ queryKey: reportKeys.all })` las refresca. Con dos
   * raíces separadas habría que acordarse de invalidar ambas, y el olvido
   * típico es el que deja la tabla de corridas mostrando el estado anterior
   * justo después de apretar «correr ahora».
   */
  schedules: (limit: number) => [...reportKeys.all, 'schedules', { limit }] as const,
  runs: (limit: number) => [...reportKeys.all, 'runs', { limit }] as const,
};
